import { create } from "zustand";
import { useConfigStore } from "./Config";
import { HttpHelpCenterService } from "../Services/http.clients/http.helpcenter.client";
import { useSessionStore } from "./Session";
import { useRouterStore } from "./Router";
import Communicator from "../Helper/Communicator";
import { helpCenterDefaultHost } from "../Helper/HelpCenterUrl";

export const getHelpCenterLanguage = (returnFullLanguage: boolean = false) => {
  const config = useConfigStore.getState().config;
  const lang = useConfigStore.getState().getCurrentLanguage();
  const localization = config?.helpcenterConfig?.localization ?? [];

  if (localization.length === 0) {
    return lang;
  }

  const match = localization.find(
    (l: any) =>
      l.language === lang || l.language?.startsWith(lang?.substring(0, 2)),
  );

  if (returnFullLanguage) {
    return match ? match : localization[0];
  }

  return match ? match.language : localization[0].language.substring(0, 2);
};

/**
 * Resolve a help center UI string. Custom wording set in the dashboard
 * (Help center -> Settings -> Translations) lives in
 * helpcenterConfig.staticTranslations[lang][key] and wins over the widget's
 * own staticTranslations (`messengerKey`, defaults to `key`). `fallback` is
 * used when neither has a value.
 */
export const translateHelpCenterText = (
  key: string,
  messengerKey: string = key,
  fallback?: string,
): string => {
  const config = useConfigStore.getState().config;
  const overrides = config?.helpcenterConfig?.staticTranslations ?? {};
  const lang = getHelpCenterLanguage();
  const short = lang?.substring(0, 2);
  const override = overrides[lang] ?? overrides[short] ?? {};
  const custom = override[key];
  if (typeof custom === "string" && custom.trim().length > 0) {
    return custom;
  }

  const translated = useConfigStore.getState().translateText(messengerKey);
  if (translated && translated !== messengerKey) {
    return translated;
  }
  return fallback ?? translated;
};

interface HelpCenterState {
  topArticles: any[];
  collections: any[];
  currentCollection: any | undefined;
  currentArticle: any | undefined;
  searchResults: any[];
  searchTerm: string;
  /** Article search in flight (searchArticles). */
  searchLoading: boolean;
  aiAnswer: string | null;
  aiSources: any[];
  aiLoading: boolean;
  /** The question Kai's answer belongs to ('' when none was asked). */
  aiQuestion: string;
  requiresPassword: boolean;
  password: string;
  passwordError: boolean;
  passwordLoading: boolean;
  submitPassword: (password: string) => Promise<void>;
  loadTopArticles: () => void;
  loadCollections: () => void;
  loadCollection: (collectionId: string) => Promise<any>;
  loadArticle: (articleId: string) => Promise<any>;
  searchArticles: (searchTerm: string) => Promise<void>;
  searchForAnswer: (question: string) => Promise<void>;
  setSearchTerm: (searchTerm: string) => void;
  clearSearch: () => void;
  clearAISearch: () => void;
  getHelpcenterTitle: () => string;
  getHelpCenterDomain: (options?: {
    includeLanguage?: boolean;
  }) => string | null;
  getHelpCenterConfig: () => any;
  openArticle: (articleId: string) => void;
}

export const useHelpCenterStore = create<HelpCenterState>()((set, get) => ({
  collections: [],
  topArticles: [],
  currentCollection: undefined,
  currentArticle: undefined,
  searchResults: [],
  searchTerm: "",
  searchLoading: false,
  aiAnswer: null,
  aiSources: [],
  aiLoading: false,
  aiQuestion: "",
  requiresPassword: false,
  password: "",
  passwordError: false,
  passwordLoading: false,
  getHelpcenterTitle: () => {
    const lang = getHelpCenterLanguage(true);

    return lang?.title ?? "Help center";
  },
  getHelpCenterConfig: () => {
    return useConfigStore.getState().config?.helpcenterConfig ?? {};
  },
  getHelpCenterDomain: ({ includeLanguage } = {}) => {
    const config = useConfigStore.getState().config;
    const language = includeLanguage ? `/${getHelpCenterLanguage()}` : "";

    if (config?.customDomainHelpCenter) {
      return `https://${config?.customDomainHelpCenter}${language}`;
    }

    if (config?.defaultDomainHelpCenter) {
      return `https://${helpCenterDefaultHost(config)}${language}`;
    }

    return null;
  },
  openArticle: (articleId: string) => {
    const routes = useRouterStore.getState().history;
    if (routes.length === 0) {
      // Additionally push helpcenter to the history to hide the tab bar
      useRouterStore.getState().pushPage("helpcenter");
    }
    useRouterStore.getState().pushPage("helpcenter-article", { id: articleId });
    Communicator.pageChanged("appextended");
  },
  setSearchTerm: (searchTerm: string) => {
    set({ searchTerm });
  },

  clearSearch: () => {
    set({ searchTerm: "", searchResults: [] });
  },

  clearAISearch: () => {
    set({ aiAnswer: null, aiSources: [], aiLoading: false, aiQuestion: "" });
  },

  async loadCollections() {
    const service = HttpHelpCenterService.getInstance();
    const lang = getHelpCenterLanguage();

    try {
      const data = await service.getCollections(lang);
      if (data?.data) {
        set({ collections: data.data, requiresPassword: false });
      }
    } catch (err: any) {
      if (err?.status === 401) {
        set({ requiresPassword: true });
      }
    }
  },

  async loadCollection(collectionId: string) {
    const service = HttpHelpCenterService.getInstance();
    const lang = getHelpCenterLanguage();

    try {
      const data = await service.getCollection(collectionId, lang);
      if (data?.data) {
        set({ currentCollection: data.data });
      }
      return data;
    } catch (err: any) {
      if (err?.status === 401) {
        set({ requiresPassword: true });
      }
      return undefined;
    }
  },

  async loadArticle(articleId: string) {
    const service = HttpHelpCenterService.getInstance();
    const lang = getHelpCenterLanguage();

    try {
      const data = await service.getArticle(articleId, lang);
      if (data?.data) {
        set({ currentArticle: data.data });
      }
      return data;
    } catch (err: any) {
      if (err?.status === 401) {
        set({ requiresPassword: true });
      }
      return undefined;
    }
  },

  async searchArticles(searchTerm: string) {
    const service = HttpHelpCenterService.getInstance();
    const lang = getHelpCenterLanguage();

    set({ searchTerm, searchLoading: true });

    try {
      const data = await service.searchArticles(searchTerm, lang);
      const results =
        data?.data?.hits?.map((result: any) => {
          // Keep the per-hit search highlight (matched terms wrapped in <mark>,
          // HTML-escaped server-side) alongside the article so result items can
          // render it. See Server's articleHighlight.ts.
          return { ...result?.document, highlight: result?.highlight ?? null };
        }) ?? [];

      if (get().searchTerm === searchTerm) {
        set({ searchResults: results ?? [], searchLoading: false });
      }
    } catch (err: any) {
      if (err?.status === 401) {
        set({ requiresPassword: true });
      }
      if (get().searchTerm === searchTerm) {
        set({ searchResults: [], searchLoading: false });
      }
    }
  },

  async searchForAnswer(question: string) {
    // The article results stay as they are: Kai's answer shows above them.
    set({
      aiLoading: true,
      aiAnswer: null,
      aiSources: [],
      aiQuestion: question,
    });

    const service = HttpHelpCenterService.getInstance();
    const lang = getHelpCenterLanguage();
    const current = () => get().aiQuestion === question;

    try {
      const answerResponse = await service
        .getAIAnswer(question, lang)
        .catch((err: any) => {
          if (err?.status === 401) set({ requiresPassword: true });
          return null;
        });
      if (!current()) return;

      set({ aiAnswer: answerResponse?.answer ?? null, aiLoading: false });

      // Kai Support returns the sources it cited with the answer. An API from
      // before that answers without them: ask for the matching sources instead.
      if (Array.isArray(answerResponse?.sources)) {
        set({ aiSources: answerResponse.sources });
      } else if (answerResponse) {
        const sourcesResponse = await service
          .getSources(question, lang)
          .catch(() => null);
        if (current()) set({ aiSources: sourcesResponse?.sources ?? [] });
      }
    } catch (err: any) {
      if (current()) set({ aiLoading: false });
    }
  },

  async loadTopArticles() {
    const lang = getHelpCenterLanguage();
    const sessionStore = useSessionStore.getState();

    try {
      var xhr = new XMLHttpRequest();
      xhr.open(
        "GET",
        sessionStore.apiUrl +
          "/outbound/helparticles/recommendations" +
          "?lang=" +
          lang,
      );
      if (sessionStore.session) {
        xhr.setRequestHeader("Api-Token", sessionStore.sdkKey);
        xhr.setRequestHeader("Gleap-Id", sessionStore.session.gleapId ?? "");
        xhr.setRequestHeader(
          "Gleap-Hash",
          sessionStore.session.gleapHash ?? "",
        );
      }
      // A password-protected help center lists no top articles without it.
      const { password } = get();
      if (password) {
        xhr.setRequestHeader("Password", password);
      }
      xhr.onreadystatechange = function () {
        if (xhr.readyState == 4 && xhr.status == 200 && xhr.responseText) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data) {
              set({
                topArticles: data,
              });
            } else {
              set({ topArticles: [] });
            }
          } catch (exp) {
            set({ topArticles: [] });
          }
        }
      };
      xhr.send();
    } catch (err: any) {
      set({ topArticles: [] });
    }
  },

  async submitPassword(enteredPassword: string) {
    set({
      passwordError: false,
      passwordLoading: true,
      password: enteredPassword,
    });

    const service = HttpHelpCenterService.getInstance();
    const lang = getHelpCenterLanguage();

    try {
      const data = await service.getCollections(lang);
      if (data?.data) {
        set({
          requiresPassword: false,
          passwordLoading: false,
          passwordError: false,
          collections: data.data,
        });
        get().loadTopArticles();
      }
    } catch (_: any) {
      set({
        password: "",
        passwordError: true,
        passwordLoading: false,
      });
    }
  },
}));
